import { useEffect, useId, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { fileReport, type ReportPlace } from "../api";
import { useT } from "../i18n";
import MutationError from "./MutationError";
import SheetHeader from "./SheetHeader";

/**
 * ReportDialog is the one dialog every report opens (requirement 0122): a line,
 * a picture or a portrait, an optional note, and Send. Sending with no note is
 * a report. Once sent it says what happens next, then closes on Done.
 *
 * Offered only to a signed-in reader: each caller checks the session before
 * showing the control that opens this.
 */
export default function ReportDialog({
  bookID,
  place,
  edition,
  about,
  onClose,
}: {
  bookID: string;
  place: ReportPlace;
  /** The published edition the reader is on, when there is one. */
  edition?: number;
  /** What is being reported, in the reader's words: the line, or a name. */
  about: string;
  onClose: () => void;
}) {
  const t = useT();
  const [note, setNote] = useState("");
  const noteID = useId();
  const field = useRef<HTMLTextAreaElement>(null);
  const send = useMutation({
    mutationFn: () => fileReport(bookID, { ...place, note: note.trim() || undefined, edition }),
  });
  useEffect(() => field.current?.focus(), []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t.report.title}
        data-testid="report-dialog"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-card border border-line bg-surface shadow-lg sm:rounded-card"
      >
        <SheetHeader title={t.report.title} onClose={onClose} closeTestID="report-close" />
        <div className="space-y-3 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {/* Authored text: its own block, the one element deciding its
              direction. */}
          <p dir="auto" data-testid="report-about" className="line-clamp-3 text-sm text-muted">
            {about}
          </p>
          {send.isSuccess ? (
            <>
              <p data-testid="report-sent" className="text-sm leading-6">
                {t.report.sent}
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  data-testid="report-done"
                  onClick={onClose}
                  className="rounded-control bg-sage px-4 py-2 text-sm font-medium text-on-sage hover:bg-sage-deep"
                >
                  {t.report.done}
                </button>
              </div>
            </>
          ) : (
            <>
              <label htmlFor={noteID} className="sr-only">
                {t.report.note}
              </label>
              <textarea
                id={noteID}
                ref={field}
                data-testid="report-note"
                dir="auto"
                rows={3}
                maxLength={2000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t.report.note}
                className="w-full resize-none rounded-control border border-line bg-surface px-3 py-2 text-sm"
              />
              <MutationError testId="report-error" error={send.error} fallback={t.report.failed} />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-control px-4 py-2 text-sm text-ink-soft hover:bg-sunk"
                >
                  {t.report.cancel}
                </button>
                <button
                  type="button"
                  data-testid="report-send"
                  disabled={send.isPending}
                  onClick={() => send.mutate()}
                  className="rounded-control bg-sage px-4 py-2 text-sm font-medium text-on-sage hover:bg-sage-deep disabled:opacity-50"
                >
                  {send.isPending ? t.report.sending : t.report.send}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
